import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as AreaIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import { formatGBP, getImageUrl, getPropertyListingById } from '../services/api';

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadProperty = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await getPropertyListingById(id);
        setProperty(response?.data || null);
      } catch (err) {
        console.error('Failed to load property details:', err);
        setError('Failed to load property details');
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [id]);

  const images = useMemo(() => {
    if (!property) return [];

    const rows = Array.isArray(property.images) ? property.images : [];
    const imagePaths = rows.length > 0
      ? rows.map((img) => getImageUrl(img.imagePath || img.ImagePath || property.primaryImagePath || '/Gallery/Items/lobby-1.jpg'))
      : [getImageUrl(property.primaryImagePath || '/Gallery/Items/lobby-1.jpg')];

    return imagePaths.filter(Boolean);
  }, [property]);

  const featureList = useMemo(() => {
    return String(property?.features || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }, [property]);

  const amenitiesList = useMemo(() => {
    return String(property?.amenities || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }, [property]);

  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const handleInquiry = () => {
    navigate('/contact', {
      state: {
        propertyInquiry: {
          id: property?.id,
          title: property?.title,
          listingCode: property?.listingCode,
          location: property?.location,
          price: property?.price,
          purpose: property?.purpose,
          propertyType: property?.propertyType,
        },
      },
    });
  };

  const handleBookNow = () => {
    navigate(`/properties/${property?.id}/book`, {
      state: {
        property,
        bookingType: property?.purpose || property?.category || 'Rent',
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  if (!property || error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 16 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Property not found</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>{error || 'This property listing could not be loaded.'}</Typography>
          <Button component={Link} to="/properties" variant="contained">Back to Properties</Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 14, pb: 8 }}>
      <Seo
        title={property?.title || 'Property Details'}
        description={property?.description || property?.subtitle || 'View property details, pricing and amenities.'}
        keywords={`property details, ${property?.propertyType || 'property'}, ${property?.purpose || 'listing'}, ${property?.location || ''}`}
      />

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography component={Link} to="/properties" sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600 }}>
            ← Back to Properties
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 4, overflow: 'hidden', mb: 4 }}>
              <Box sx={{ position: 'relative' }}>
                <Box component="img" src={images[currentImageIndex]} alt={property.title} sx={{ width: '100%', height: { xs: 280, md: 520 }, objectFit: 'cover' }} />
                {images.length > 1 ? (
                  <>
                    <IconButton onClick={prevImage} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}>
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton onClick={nextImage} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}>
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                ) : null}
              </Box>

              {images.length > 1 ? (
                <Stack direction="row" spacing={1} sx={{ p: 2, overflowX: 'auto' }}>
                  {images.map((image, index) => (
                    <Box
                      key={`${image}-${index}`}
                      component="img"
                      src={image}
                      alt={`Property view ${index + 1}`}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 88,
                        height: 72,
                        borderRadius: 2,
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border: index === currentImageIndex ? '3px solid #2563eb' : '2px solid transparent',
                      }}
                    />
                  ))}
                </Stack>
              ) : null}
            </Card>

            <Card sx={{ borderRadius: 4, p: 4 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip label={property.category || property.purpose || 'Property'} color="primary" />
                <Chip label={property.propertyType || 'Type'} variant="outlined" />
                {property.isFeatured ? <Chip label="Featured" color="secondary" /> : null}
                {property.isPremium ? <Chip label="Premium" sx={{ bgcolor: '#ede9fe', color: '#6d28d9' }} /> : null}
              </Stack>

              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>{property.title}</Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>{property.subtitle || property.description}</Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3, color: 'text.secondary', flexWrap: 'wrap' }}>
                <LocationIcon fontSize="small" />
                <Typography variant="body1">{property.location}{property.city ? `, ${property.city}` : ''}{property.country ? `, ${property.country}` : ''}</Typography>
              </Stack>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {property.bedrooms ? <Grid item xs={6} md={3}><Card sx={{ p: 2.5, textAlign: 'center', bgcolor: '#f8fafc' }}><BedIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 700 }}>{property.bedrooms}</Typography><Typography variant="body2" color="text.secondary">Bedrooms</Typography></Card></Grid> : null}
                {property.bathrooms ? <Grid item xs={6} md={3}><Card sx={{ p: 2.5, textAlign: 'center', bgcolor: '#f8fafc' }}><BathIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 700 }}>{property.bathrooms}</Typography><Typography variant="body2" color="text.secondary">Bathrooms</Typography></Card></Grid> : null}
                {property.areaSize ? <Grid item xs={6} md={3}><Card sx={{ p: 2.5, textAlign: 'center', bgcolor: '#f8fafc' }}><AreaIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 700 }}>{property.areaSize}</Typography><Typography variant="body2" color="text.secondary">{property.areaUnit || 'sq ft'}</Typography></Card></Grid> : null}
                {property.yearBuilt ? <Grid item xs={6} md={3}><Card sx={{ p: 2.5, textAlign: 'center', bgcolor: '#f8fafc' }}><CalendarIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 700 }}>{property.yearBuilt}</Typography><Typography variant="body2" color="text.secondary">Year Built</Typography></Card></Grid> : null}
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Description</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 4 }}>
                {property.description || 'No detailed description is available for this property yet.'}
              </Typography>

              {featureList.length > 0 ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Features</Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {featureList.map((item) => (
                      <Grid item xs={12} md={6} key={item}>
                        <Card sx={{ p: 2, bgcolor: '#f8fafc' }}>
                          <Typography variant="body1">{item}</Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : null}

              {amenitiesList.length > 0 ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Amenities</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {amenitiesList.map((item) => (
                      <Chip key={item} label={item} variant="outlined" />
                    ))}
                  </Stack>
                </>
              ) : null}
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 4, p: 4, position: { lg: 'sticky' }, top: 120 }}>
              <Typography variant="body2" color="text.secondary">Listing Code</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>{property.listingCode}</Typography>

              <Typography variant="body2" color="text.secondary">Price</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                {formatGBP(property.price || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {property.purpose || property.category || 'Property Listing'}
              </Typography>

              {property.originalPrice ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Original Price: {formatGBP(property.originalPrice)}
                </Typography>
              ) : null}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Contact Information</Typography>
              <Stack spacing={1.5} sx={{ mb: 4 }}>
                {property.contactName ? <Typography variant="body1">{property.contactName}</Typography> : null}
                {property.contactPhone ? <Stack direction="row" spacing={1} alignItems="center"><PhoneIcon fontSize="small" color="primary" /><Typography variant="body2">{property.contactPhone}</Typography></Stack> : null}
                {property.contactEmail ? <Stack direction="row" spacing={1} alignItems="center"><EmailIcon fontSize="small" color="primary" /><Typography variant="body2">{property.contactEmail}</Typography></Stack> : null}
              </Stack>

              <Button fullWidth variant="contained" size="large" sx={{ mb: 2 }} onClick={handleInquiry}>
                Send Inquiry
              </Button>
              <Button fullWidth variant="contained" color="secondary" size="large" sx={{ mb: 2 }} onClick={handleBookNow}>
                Book & Pay Now
              </Button>
              <Button fullWidth variant="outlined" size="large" component={Link} to="/properties">
                Browse More Properties
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PropertyDetails;
